// Sorgente unica del grafo del corpus RAG.
// REGOLA: gli archi "contiene" NON si scrivono a mano. Si derivano da nodo.corpus
// (vedi il ciclo in fondo): altrimenti una fonte può dichiararsi membro di un
// corpus senza esservi collegata e il grafo mostra nodi sospesi nel vuoto.

export type TipoNodo = 'corpus' | 'ue' | 'naz' | 'reg' | 'prassi' | 'atto';
export type Rel = 'contiene' | 'attua' | 'cita' | 'abroga';

export interface Nodo {
  id: string;
  label: string;
  tipo: TipoNodo;
  corpus: string[];
  chunk: number;
  cit: number;
  vigente: boolean;
  desc: string;
}

export type Arco = [string, string, Rel];

export const COLORI: Record<TipoNodo, string> = {
  corpus: '#2b2a72', ue: '#0b5fbf', naz: '#1d6b48',
  reg: '#a06a00', prassi: '#5d6472', atto: '#7b3fd4',
};
export const NOMI_TIPO: Record<TipoNodo, string> = {
  corpus: 'CORPUS', ue: 'FONTE UE', naz: 'FONTE NAZIONALE',
  reg: 'FONTE REGIONALE', prassi: 'LINEE GUIDA E PRASSI', atto: 'ATTO PRODOTTO',
};
export const COL_ARCO: Record<Rel, string> = { contiene: '#2b2a72', attua: '#0b5fbf', cita: '#9aa0ad', abroga: '#c0392b' };

export const NODI: Nodo[] = [
  { id: 'c-fesr', label: 'Corpus FESR', tipo: 'corpus', corpus: ['fesr'], chunk: 86402, cit: 0, vigente: true, desc: 'Snapshot v12 pubblicato il 05.09.2026. 1.284 documenti.' },
  { id: 'c-fse', label: 'Corpus FSE+', tipo: 'corpus', corpus: ['fse'], chunk: 61130, cit: 0, vigente: true, desc: 'Snapshot v8. Reindicizzazione in corso al 41%.' },
  { id: 'c-feasr', label: 'Corpus FEASR', tipo: 'corpus', corpus: ['feasr'], chunk: 19884, cit: 0, vigente: true, desc: 'Snapshot v4. 14 documenti fuori dal corpus per errore OCR.' },

  { id: 'ue-1060', label: 'Reg. (UE) 2021/1060', tipo: 'ue', corpus: ['fesr', 'fse', 'feasr'], chunk: 1840, cit: 184, vigente: true, desc: 'Disposizioni comuni sui fondi. È la fonte più citata del corpus.' },
  { id: 'ue-1058', label: 'Reg. (UE) 2021/1058', tipo: 'ue', corpus: ['fesr'], chunk: 720, cit: 61, vigente: true, desc: 'FESR e Fondo di coesione.' },
  { id: 'ue-1057', label: 'Reg. (UE) 2021/1057', tipo: 'ue', corpus: ['fse'], chunk: 690, cit: 98, vigente: true, desc: 'Fondo sociale europeo Plus.' },
  { id: 'ue-2115', label: 'Reg. (UE) 2021/2115', tipo: 'ue', corpus: ['feasr'], chunk: 810, cit: 44, vigente: true, desc: 'Piani strategici della PAC e sviluppo rurale.' },
  { id: 'ue-1689', label: 'Reg. (UE) 2024/1689', tipo: 'ue', corpus: ['fesr', 'fse', 'feasr'], chunk: 460, cit: 23, vigente: true, desc: 'AI Act. Fonda gli obblighi di trasparenza della piattaforma.' },
  { id: 'ue-679', label: 'Reg. (UE) 2016/679', tipo: 'ue', corpus: ['fesr', 'fse', 'feasr'], chunk: 380, cit: 31, vigente: true, desc: 'GDPR. Vincola il trattamento nei modelli.' },
  { id: 'ue-1303', label: 'Reg. (UE) 1303/2013', tipo: 'ue', corpus: ['fesr', 'fse'], chunk: 240, cit: 9, vigente: false, desc: 'Programmazione 2014–2020. Non citabile come base giuridica attiva.' },

  { id: 'it-241', label: 'L. 241/1990', tipo: 'naz', corpus: ['fesr', 'fse', 'feasr'], chunk: 620, cit: 126, vigente: true, desc: 'Procedimento amministrativo. Motivazione e accesso agli atti.' },
  { id: 'it-36', label: 'D.lgs. 36/2023', tipo: 'naz', corpus: ['fesr', 'fse'], chunk: 940, cit: 74, vigente: true, desc: 'Codice dei contratti pubblici.' },
  { id: 'it-132', label: 'L. 132/2025', tipo: 'naz', corpus: ['fesr', 'fse', 'feasr'], chunk: 210, cit: 18, vigente: true, desc: 'Disposizioni nazionali sull\'intelligenza artificiale.' },
  { id: 'it-82', label: 'D.lgs. 82/2005', tipo: 'naz', corpus: ['fesr', 'fse', 'feasr'], chunk: 300, cit: 27, vigente: true, desc: 'Codice dell\'amministrazione digitale.' },
  { id: 'it-163', label: 'D.lgs. 163/2006', tipo: 'naz', corpus: ['fesr'], chunk: 180, cit: 4, vigente: false, desc: 'Vecchio codice appalti, abrogato dal D.lgs. 36/2023.' },

  { id: 'rg-pr-fesr', label: 'PR FESR 2021–2027', tipo: 'reg', corpus: ['fesr'], chunk: 1120, cit: 68, vigente: true, desc: 'Programma regionale approvato con decisione della Commissione.' },
  { id: 'rg-pr-fse', label: 'PR FSE+ 2021–2027', tipo: 'reg', corpus: ['fse'], chunk: 980, cit: 57, vigente: true, desc: 'Programma regionale FSE+.' },
  { id: 'rg-psr', label: 'Piano di sviluppo rurale', tipo: 'reg', corpus: ['feasr'], chunk: 760, cit: 22, vigente: true, desc: 'Complemento regionale per lo sviluppo rurale.' },
  { id: 'rg-avv-2', label: 'Avviso 2/2026 formazione', tipo: 'reg', corpus: ['fse'], chunk: 140, cit: 34, vigente: true, desc: 'Avviso attuativo: criteri di ammissibilità applicati in istruttoria.' },
  { id: 'rg-dgr-88', label: 'Delibera 88/2024', tipo: 'reg', corpus: ['fesr'], chunk: 60, cit: 2, vigente: false, desc: 'Sostituita dalla delibera 114/2026.' },
  { id: 'rg-dgr-114', label: 'Delibera 114/2026', tipo: 'reg', corpus: ['fesr'], chunk: 90, cit: 16, vigente: true, desc: 'Criteri di selezione delle operazioni.' },

  { id: 'lg-igrue', label: 'Linee guida IGRUE', tipo: 'prassi', corpus: ['fesr', 'fse', 'feasr'], chunk: 330, cit: 29, vigente: true, desc: 'Prassi su ammissibilità della spesa e controlli.' },
  { id: 'lg-audit', label: 'Manuale dei controlli', tipo: 'prassi', corpus: ['fesr', 'fse'], chunk: 280, cit: 41, vigente: true, desc: 'Check-list usate in verifica di conformità.' },
  { id: 'lg-agid', label: 'Linee guida AGID', tipo: 'prassi', corpus: ['fesr', 'fse', 'feasr'], chunk: 190, cit: 11, vigente: true, desc: 'Requisiti tecnici e di accessibilità.' },

  { id: 'at-avviso-pmi', label: 'Avviso efficienza PMI', tipo: 'atto', corpus: ['fesr'], chunk: 0, cit: 0, vigente: true, desc: 'In redazione al 72%. Due redattori, modello on-premise.' },
  { id: 'at-dec-412', label: 'Decreto 412/2026', tipo: 'atto', corpus: ['fse'], chunk: 0, cit: 0, vigente: true, desc: 'Bozza: manca la base giuridica per l\'Allegato B.' },
  { id: 'at-det-407', label: 'Determina 407/2026', tipo: 'atto', corpus: ['fesr'], chunk: 0, cit: 0, vigente: true, desc: 'Validata. Pronta per il protocollo.' },
  { id: 'at-ver-9', label: 'Verbale istruttoria 9', tipo: 'atto', corpus: ['fse'], chunk: 0, cit: 0, vigente: true, desc: 'Prodotto dalla skill di verifica di conformità.' },
];

export const ARCHI: Arco[] = [
  ['rg-pr-fesr', 'ue-1058', 'attua'], ['rg-pr-fse', 'ue-1057', 'attua'], ['rg-psr', 'ue-2115', 'attua'],
  ['rg-avv-2', 'rg-pr-fse', 'attua'], ['rg-dgr-114', 'ue-1060', 'attua'], ['it-132', 'ue-1689', 'attua'],
  ['lg-igrue', 'ue-1060', 'attua'], ['lg-audit', 'ue-1060', 'attua'],

  ['it-36', 'it-163', 'abroga'], ['rg-dgr-114', 'rg-dgr-88', 'abroga'], ['ue-1060', 'ue-1303', 'abroga'],

  ['at-avviso-pmi', 'ue-1058', 'cita'], ['at-avviso-pmi', 'rg-pr-fesr', 'cita'], ['at-avviso-pmi', 'it-241', 'cita'],
  ['at-dec-412', 'ue-1060', 'cita'], ['at-dec-412', 'rg-avv-2', 'cita'], ['at-dec-412', 'it-241', 'cita'],
  ['at-det-407', 'it-36', 'cita'], ['at-det-407', 'rg-dgr-114', 'cita'], ['at-det-407', 'lg-igrue', 'cita'],
  ['at-ver-9', 'lg-audit', 'cita'], ['at-ver-9', 'ue-1057', 'cita'],
];

export const CORPUS_ID: Record<string, string> = { fesr: 'c-fesr', fse: 'c-fse', feasr: 'c-feasr' };

// Gli archi "contiene" sono DERIVATI dall'appartenenza dichiarata sul nodo:
// una fonte non può risultare membro di un corpus senza esservi collegata.
for (const n of NODI) {
  if (n.tipo === 'corpus' || n.tipo === 'atto') continue;
  for (const c of n.corpus) {
    const cid = CORPUS_ID[c];
    if (cid) ARCHI.push([cid, n.id, 'contiene']);
  }
}

export const NOME_CORPUS: Record<string, string> = { fesr: 'FESR', fse: 'FSE+', feasr: 'FEASR' };
